import React, { useState } from 'react';
import { PlanConfig } from '../../types/Calculadora';
import { Button, Modal } from '../ui';

interface Props {
  /** Planes del escenario, para elegir de cuál copiar precios y cobertura. */
  planes: PlanConfig[];
  /** Plan preseleccionado como base. `null` arranca en blanco. */
  baseSugerida: string | null;
  onCrear: (nombre: string, basePlanId: string | null) => void;
  onClose: () => void;
}

const EN_BLANCO = '';

/**
 * Alta de un plan ficticio: un plan que no está en el catálogo pero se calcula
 * igual que los demás, para simular precios o coberturas que todavía no existen.
 */
const NuevoPlanModal: React.FC<Props> = ({ planes, baseSugerida, onCrear, onClose }) => {
  const [nombre, setNombre] = useState('');
  const [base, setBase] = useState<string>(baseSugerida ?? EN_BLANCO);

  const limpio = nombre.trim();
  // El prellenado con datos reales machea por nombre de plan: dos planes con el
  // mismo nombre se llevarían los mismos suscriptores de Salesforce.
  const repetido = planes.some((p) => p.nombre.toLowerCase() === limpio.toLowerCase());
  const valido = limpio !== '' && !repetido;

  const confirmar = () => {
    if (!valido) return;
    onCrear(limpio, base === EN_BLANCO ? null : base);
  };

  return (
    <Modal titulo="Nuevo plan ficticio" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Se calcula igual que los planes reales y entra al consolidado, pero no existe en el
          catálogo: sirve para probar una cuota, una cobertura o una prestación nueva.
        </p>

        <div>
          <label htmlFor="plan-nombre" className="block text-xs font-semibold text-gray-600 mb-1.5">
            Nombre del plan
          </label>
          <input
            id="plan-nombre"
            type="text"
            value={nombre}
            autoFocus
            maxLength={60}
            placeholder="Ej: Classic 2027"
            onChange={(e) => setNombre(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && confirmar()}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2
              focus:outline-none focus:ring-2 focus:ring-gappex-primary/30 focus:border-gappex-primary"
          />
          {repetido && (
            <p className="mt-1.5 text-[11px] text-amber-600 leading-snug">
              Ya hay un plan con ese nombre. Poné otro para no mezclarlo al traer los datos reales.
            </p>
          )}
        </div>

        <div>
          <label htmlFor="plan-base" className="block text-xs font-semibold text-gray-600 mb-1.5">
            Copiar precios y cobertura de
          </label>
          <select
            id="plan-base"
            value={base}
            onChange={(e) => setBase(e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white
              focus:outline-none focus:ring-2 focus:ring-gappex-primary/30 focus:border-gappex-primary"
          >
            <option value={EN_BLANCO}>— En blanco (precios de lista, sin descuentos) —</option>
            {planes.map((p) => (
              <option key={p.planId} value={p.planId}>
                {p.nombre}
              </option>
            ))}
          </select>
          <p className="mt-1.5 text-[11px] text-gray-400 leading-snug">
            Los suscriptores arrancan en cero: si copiara la población del plan base, el consolidado
            la contaría dos veces.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" variant="primary" onClick={confirmar} disabled={!valido}>
            Crear plan
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default NuevoPlanModal;
